import React, { lazy, Suspense, useContext } from 'react';
import { Redirect, Route, Switch } from 'react-router-dom';
import BuyerListPage from 'pages/Referentiels/Buyers';
import ProductionEdit from 'pages/Productions/ProductionEdit';
import Layout from 'components/Layout';
import ReceptionListPage from 'pages/Receptions';
import ProductionListPage from 'pages/Productions';
import ReceptionCreate from 'pages/Receptions/ReceptionCreate';
import ReceptionEdit from 'pages/Receptions/ReceptionEdit';
import ReceptionShow from 'pages/Receptions/ReceptionShow';
import ExpeditionCreate from 'pages/Expeditions/ExpeditionCreate';
import ExpeditionShow from 'pages/Expeditions/ExpeditionShow';
import ExpeditionEdit from 'pages/Expeditions/ExpeditionEdit';
import ProductionCreate from 'pages/Productions/ProductionCreate';
import ProductionShow from 'pages/Productions/ProductionShow';
import HomePage from 'pages/Home/HomePage';
import Loading from 'components/Loading';
import { AppContext } from 'context';
import ClosureListPage from 'pages/Closures';
import ClosureSuccess from 'pages/Closures/ClosureSuccess';
import GlobalReportsPage from 'pages/Reports/Global';
import { MenuProvider } from 'menuContext';
import CharacterizationListPage from 'pages/Characterizations';
import CharacterizationCreate from 'pages/Characterizations/CharacterizationCreate';
import ClosureClose from 'pages/Closures/ClosureClose';
import EstimatedReceptionListPage from 'pages/EstimatedReceptions';
import ExpeditionListPage from 'pages/Expeditions';
import Login from 'pages/Login';
import ChangePasswordForm from 'pages/Login/ChangePasswordForm';
import LostPasswordForm from 'pages/Login/LostPasswordForm';
import PrivacyPolicy from 'pages/PrivacyPolicy';
import ClientReportsPage from 'pages/Reports/Client';
import ConditionedStockPage from 'pages/Stocks/Conditioned';
import TermsOfUse from 'pages/TermsOfUse';
import { Routes } from 'routes';
import { UserRole } from 'types';
import CharacterizationShow from 'pages/Characterizations/CharacterizationShow';
import CharacterizationEdit from 'pages/Characterizations/CharacterizationEdit';
import ConditionedByClientPage from 'pages/Stocks/ConditionedByClient';
import DocumentListPage from 'pages/Documents';
import ClientListPage from 'pages/Referentiels/Clients';
import SortingCenterSelectionPage from 'pages/SortingCenterSelection/SortingCenterSelectionPage';
import ClientShow from 'pages/Referentiels/Clients/ClientShow';
import BuyerShow from 'pages/Referentiels/Buyers/BuyerShow';
import MySortingCenter from 'pages/Referentiels/MySortingCenter';
import InboundStockPage from 'pages/Stocks/Inbound';
import UserProfilePage from 'pages/UserProfile';
import BuyerCreate from 'pages/Referentiels/Buyers/BuyerCreate';
import ClientBuyerContractsCreatePage from 'pages/Referentiels/Clients/ClientBuyerContractsCreatePage';
import ClientCreate from 'pages/Referentiels/Clients/ClientCreate';
import SortingCenterContractCreate from 'pages/Referentiels/Clients/SortingCenterContractCreate';
import SortingCenterContractEdit from 'pages/Referentiels/Clients/SortingCenterContractEdit';
import InfoPage from 'pages/Info';
import StockConditionneParRepreneurPage from 'pages/Stocks/ContionneParRepreneur';
import AdministrationUsers from './pages/Administration/AdministrationUsers';
import AdministrationHomepage from './pages/Administration/AdministrationHomepage';
import AdministrationUserCreate from './pages/Administration/AdministrationUserCreate';
import AdministrationUserShow from './pages/Administration/AdministrationUserShow';
import AdministrationAddSortingCenterUserRole from './pages/Administration/AdministrationAddSortingCenterUserRole';

const AdminHydraProvider = lazy(() => import('components/AdminDashboard/AdminHydraProvider'));

const AppRoutes: React.FC = () => {
  const context = useContext(AppContext);
  const userRoles = context.getRoles();

  return (
    <Switch>
      <Route path="/cgu" component={TermsOfUse} />
      <Route path="/politique-de-confidentialite" component={PrivacyPolicy} />
      <Route path="/reset-password/:token" component={ChangePasswordForm} />
      <Route path="/reset-password" component={LostPasswordForm} />
      <Route path={Routes.InfoPage} component={InfoPage} />

      {userRoles === null || !userRoles.includes(UserRole.ROLE_ACCEPTED_TOS) ? (
        <Route exact path="/" component={Login} />
      ) : (
        <Switch>
          <Route path={Routes.SortingCenterSelection} component={SortingCenterSelectionPage} />
          <Route path={Routes.UserProfile} component={UserProfilePage} />

          {context.hasLeastOneAdminRole() && (
            <Route path={Routes.AdministrationUserCreate} component={AdministrationUserCreate} />
          )}
          {context.hasLeastOneAdminRole() && (
            <Route
              path={Routes.AdministrationUserSortingCenterUserRole}
              component={AdministrationAddSortingCenterUserRole}
            />
          )}
          {context.hasLeastOneAdminRole() && (
            <Route path={Routes.AdministrationUserShow} component={AdministrationUserShow} />
          )}
          {context.hasLeastOneAdminRole() && (
            <Route path={Routes.AdministrationUsers} component={AdministrationUsers} />
          )}
          {context.hasLeastOneAdminRole() && (
            <Route path={Routes.AdministrationHomepage} component={AdministrationHomepage} />
          )}

          {context.getSortingCenter?.() || userRoles?.includes(UserRole.ROLE_SUPER_ADMIN) ? (
            <Route>
              <MenuProvider>
                <Layout>
                  <Switch>
                    {userRoles?.includes(UserRole.ROLE_SUPER_ADMIN) && (
                      <Route path={Routes.Admin}>
                        <Suspense fallback={<Loading />}>
                          <AdminHydraProvider />
                        </Suspense>
                      </Route>
                    )}

                    {(context.hasLeastOneAdminRole() || userRoles?.includes(UserRole.ROLE_MANAGER)) && (
                      <Route path={Routes.BuyersCreate} component={BuyerCreate} />
                    )}

                    <Route path={Routes.ExpeditionCreate} component={ExpeditionCreate} />
                    <Route path={Routes.EstimatedReceptionsEdit} component={EstimatedReceptionListPage} />
                    <Route path={Routes.EstimatedReceptions} component={EstimatedReceptionListPage} />

                    <Route path={Routes.ExpeditionEdit} component={ExpeditionEdit} />
                    <Route path={Routes.ExpeditionShow} component={ExpeditionShow} />
                    <Route path={Routes.Expeditions} component={ExpeditionListPage} />

                    <Route path={Routes.CharacterizationCreate} component={CharacterizationCreate} />
                    <Route path={Routes.CharacterizationEdit} component={CharacterizationEdit} />
                    <Route path={Routes.CharacterizationShow} component={CharacterizationShow} />
                    <Route path={Routes.Characterizations} component={CharacterizationListPage} />

                    <Route path={Routes.ProductionCreate} component={ProductionCreate} />
                    <Route path={Routes.ProductionEdit} component={ProductionEdit} />
                    <Route path={Routes.ProductionShow} component={ProductionShow} />
                    <Route path={Routes.Productions} component={ProductionListPage} />

                    <Route path={Routes.ReceptionCreate} component={ReceptionCreate} />
                    <Route path={Routes.ReceptionEdit} component={ReceptionEdit} />
                    <Route path={Routes.ReceptionShow} component={ReceptionShow} />
                    <Route path={Routes.Receptions} component={ReceptionListPage} />

                    <Route path={Routes.ClosureSuccess} component={ClosureSuccess} />
                    <Route path={Routes.ClosureClose} component={ClosureClose} />
                    <Route path={Routes.Closures} component={ClosureListPage} />
                    <Route path={Routes.ClientReports} component={ClientReportsPage} />
                    <Route path={Routes.GlobalReports} component={GlobalReportsPage} />
                    <Route path={Routes.InboundStocks} component={InboundStockPage} />
                    <Route path={Routes.ConditionedStocks} component={ConditionedStockPage} />
                    <Route path={Routes.ConditionedStocksByClient} component={ConditionedByClientPage} />
                    <Route
                      path={Routes.StockConditionneParRepreneurPage}
                      component={StockConditionneParRepreneurPage}
                    />
                    <Route path={Routes.SortingCenterContractCreate} component={SortingCenterContractCreate} />
                    <Route path={Routes.SortingCenterContractUpdate} component={SortingCenterContractEdit} />
                    <Route path={Routes.ClientCreate} component={ClientCreate} />
                    <Route path={Routes.ClientShow} component={ClientShow} />
                    <Route path={Routes.Clients} component={ClientListPage} />
                    <Route path={Routes.BuyerShow} component={BuyerShow} />
                    <Route path={Routes.Buyers} component={BuyerListPage} />
                    <Route path={Routes.Documents} component={DocumentListPage} />
                    <Route path={Routes.SortingCenter} component={MySortingCenter} />

                    <Route path={Routes.ClientContracts} component={ClientBuyerContractsCreatePage} />

                    <Route component={HomePage} />
                  </Switch>
                </Layout>
              </MenuProvider>
            </Route>
          ) : (
            context.user && (
              <Route>
                <Redirect to={Routes.SortingCenterSelection} />
              </Route>
            )
          )}
        </Switch>
      )}
      <Route>
        <Redirect to="/" />
      </Route>
    </Switch>
  );
};

export default AppRoutes;
